// Service for fetching discover content powered by Groq + Pixabay + Perigon
//
import { fetchWeatherByCity, WeatherResult } from './weatherService';
import { fetchTrendingNews as fetchPerigonTrendingNews, PerigonArticle } from './perigonService';
import { getRandomSuggestions } from '@/data/chatSuggestions';

const GROQ_API_KEY = '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';
const PIXABAY_API_KEY = '47639459-e61bd10d0c87aa6e0dc661c10';

const GROQ_ENDPOINT = 'https://api.groq.com/openai/v1/chat/completions';
const PIXABAY_ENDPOINT = 'https://pixabay.com/api/';

const DEFAULT_WEATHER_CITY = import.meta.env.VITE_DEFAULT_WEATHER_CITY || 'San Francisco';

export type DiscoverMode = 'chat' | 'image' | 'web' | 'deep-research';

export interface WeatherData {
  temperature: number;
  condition: string;
  location: string;
  high: number;
  low: number;
  icon: string;
  // Extended data from WeatherAPI
  feelsLike?: number;
  humidity?: number;
  windSpeed?: number;
  windDirection?: string;
  pressure?: number;
  visibility?: number;
  uv?: number;
  cloudCover?: number;
  precipitation?: number;
  gustSpeed?: number;
  // Location details
  region?: string;
  country?: string;
  timezone?: string;
  localTime?: string;
  coordinates?: { lat: number; lon: number };
  // Forecast data
  forecast?: Array<{
    date: string;
    maxTemp: number;
    minTemp: number;
    condition: string;
    icon: string;
    precipitation: number;
    humidity: number;
  }>;
  // Astronomy
  astronomy?: {
    sunrise: string;
    sunset: string;
    moonrise: string;
    moonset: string;
    moonPhase: string;
    moonIllumination: string;
  };
}

export interface NewsItem {
  id: string;
  title: string;
  description: string;
  source: string;
  image?: string;
  url: string;
  publishedAt: string;
  authors?: string[];
  categories?: string[];
  topics?: string[];
  sentiment?: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

export interface TopicCard {
  id: string;
  title: string;
  description?: string;
  image?: string;
  category: string;
}

export interface DailyDigest {
  title: string;
  items: string[];
  date: string;
}

export type DiscoverDetailType = 'news' | 'topic' | 'weather' | 'digest';

export interface DiscoverDetailSection {
  heading: string;
  body: string;
}

export interface DiscoverDetailResource {
  title: string;
  description: string;
  url?: string;
}

export interface DiscoverDetailAction {
  label: string;
  description: string;
  url?: string;
}

export interface DiscoverDetailContent {
  summary: string;
  keyTakeaways: string[];
  sections: DiscoverDetailSection[];
  stats?: { label: string; value: string }[];
  resources?: DiscoverDetailResource[];
  actions?: DiscoverDetailAction[];
  quote?: { text: string; author?: string };
  metadata?: { readingTime?: string; tone?: string; sentiment?: string };
}

export interface ModeInsight {
  mode: DiscoverMode;
  title: string;
  summary: string;
  tips: string[];
  promptSuggestions?: string[];
  callToAction?: string;
}

interface GroqNewsItem extends NewsItem {
  imageKeyword?: string;
}

interface PerigonNewsItem extends NewsItem {
  content?: string;
}

interface GroqTopicItem extends TopicCard {
  imageKeyword?: string;
}

interface DiscoverContentPayload {
  weather: WeatherData | null;
  news: PerigonNewsItem[];
  topics: GroqTopicItem[];
  dailyDigest: DailyDigest | null;
  modeInsights: ModeInsight[];
  newsSummary?: string;
}

let cachedDiscoverContent: DiscoverContentPayload | null = null;
let pendingDiscoverRequest: Promise<DiscoverContentPayload> | null = null;
const detailCache = new Map<string, DiscoverDetailContent>();
const pendingDetailRequests = new Map<string, Promise<DiscoverDetailContent>>();

// LocalStorage keys
const STORAGE_KEY_DISCOVER_CONTENT = 'dis_ctnt';
const STORAGE_KEY_DISCOVER_TIMESTAMP = 'dis_ts';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Save discover content to localStorage
const saveToLocalStorage = (content: DiscoverContentPayload): void => {
  try {
    const { modeInsights: _omitModeInsights, ...rest } = content as any;
    localStorage.setItem(STORAGE_KEY_DISCOVER_CONTENT, JSON.stringify(rest));
    localStorage.setItem(STORAGE_KEY_DISCOVER_TIMESTAMP, Date.now().toString());
  } catch (error) {
    console.warn('Failed to load:', error);
  }
};

// Load discover content from localStorage
const loadFromLocalStorage = (): DiscoverContentPayload | null => {
  try {
    const timestamp = localStorage.getItem(STORAGE_KEY_DISCOVER_TIMESTAMP);
    const content = localStorage.getItem(STORAGE_KEY_DISCOVER_CONTENT);

    if (!timestamp || !content) {
      return null;
    }

    const age = Date.now() - parseInt(timestamp, 10);
    if (age > CACHE_DURATION_MS) {
      // Cache expired, clear it
      localStorage.removeItem(STORAGE_KEY_DISCOVER_CONTENT);
      localStorage.removeItem(STORAGE_KEY_DISCOVER_TIMESTAMP);
      return null;
    }

    const parsed = JSON.parse(content);
    return { ...(parsed || {}), modeInsights: [] } as DiscoverContentPayload;
  } catch (error) {
    console.warn('Failed to load:', error);
    return null;
  }
};

const cleanJSONContent = (content: string) =>
  content.trim().replace(/```json/gi, '').replace(/```/g, '').trim();

const ensureISODate = (value?: string) => {
  const parsed = value ? new Date(value) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
};

const fetchPixabayImage = async (keyword?: string): Promise<string | undefined> => {
  if (!keyword) return undefined;

  try {
    const response = await fetch(
      `${PIXABAY_ENDPOINT}?key=${PIXABAY_API_KEY}&q=${encodeURIComponent(keyword)}&image_type=photo&orientation=horizontal&per_page=3&safesearch=true`
    );

    if (!response.ok) {
      return undefined;
    }

    const data = await response.json();
    const hit = data?.hits?.[0];
    if (!hit) return undefined;

    return hit.largeImageURL || hit.webformatURL || undefined;
  } catch (error) {
    return undefined;
  }
};

const callGroqForDiscoverContent = async (): Promise<Omit<DiscoverContentPayload, 'news' | 'newsSummary' | 'weather'>> => {
  const response = await fetch(GROQ_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: 'openai/gpt-oss-120b',
      temperature: 0.8,
      max_completion_tokens: 4096,
      top_p: 1,
      stream: false,
      reasoning_effort: 'medium',
      messages: [
        {
          role: 'system',
          content:
            'You create structured JSON for a daily discover dashboard. Respond with JSON that matches the requested schema and nothing else.',
        },
        {
          role: 'user',
          content: `Generate discovery content for a tech-savvy professional.
Return JSON with the following shape:
{
  "dailyDigest": {
    "title": string,
    "date": string, // Month Day format, e.g. "Oct 16"
    "items": string[] // 3-5 short bullet summaries (<120 chars each)
  },
  "topics": [
    {
      "id": string,
      "title": string,
      "description": string, // <= 120 chars
      "category": string,
      "imageKeyword": string // concise keyword for relevant imagery
    }
  ],
  "modeInsights": [
    {
      "mode": "chat",
      "title": string, // maximum 3 words
      "summary": string, // 1-2 sentences describing this mode (<150 chars)
      "tips": string[],
    }
  ]
}

Guidelines:
- Provide 6 curated topics spanning learning, creativity, productivity, wellness, or lifestyle.
- Ensure all text is in English and engaging.
- Use realistic yet fictional data only when necessary.
- Tailor mode insights to help the user get the most from each mode.
- Never include markdown fences or commentary outside the JSON.`,
        },
      ],
    }),
  });

  if (!response.ok) {
    const errorPayload = await response.text();
    throw new Error(`Groq request failed: ${response.status} ${errorPayload}`);
  }

  const groqData = await response.json();
  const content: string | undefined = groqData?.choices?.[0]?.message?.content;

  if (!content) {
    throw new Error('Groq response did not include content');
  }

  const cleaned = cleanJSONContent(content);

  try {
    const parsed = JSON.parse(cleaned);

    const safeTopics = Array.isArray(parsed.topics) ? parsed.topics : [];
    const safeModeInsights = Array.isArray(parsed.modeInsights) ? parsed.modeInsights : [];

    // Add random prompt suggestions from our predefined list to each mode insight
    const modeInsightsWithSuggestions = safeModeInsights.map((insight: ModeInsight) => ({
      ...insight,
      promptSuggestions: getRandomSuggestions(4).map(s => s.text),
    }));

    return {
      dailyDigest: parsed.dailyDigest ?? null,
      topics: safeTopics,
      modeInsights: modeInsightsWithSuggestions as ModeInsight[],
    };
  } catch (error) {
    throw error;
  }
};

const convertPerigonToNewsItem = (article: PerigonArticle): PerigonNewsItem => {
  return {
    id: article.articleId,
    title: article.title,
    description: article.description || article.content?.substring(0, 160) || '',
    source: article.source.title || article.source.domain,
    image: article.imageUrl,
    url: article.url,
    publishedAt: article.pubDate,
    authors: article.authors,
    categories: article.categories,
    topics: article.topics,
    sentiment: article.sentiment,
    content: article.content,
  };
};

export const fetchDiscoverContent = async (forceRefresh = false): Promise<DiscoverContentPayload> => {
  if (!forceRefresh) {
    // First check in-memory cache
    if (cachedDiscoverContent) {
      return cachedDiscoverContent;
    }

    // Then check localStorage
    const storedContent = loadFromLocalStorage();
    if (storedContent) {
      cachedDiscoverContent = storedContent;
      return storedContent;
    }

    if (pendingDiscoverRequest) {
      return pendingDiscoverRequest;
    }
  }

  pendingDiscoverRequest = (async () => {
    try {
      const [groqData, perigonData] = await Promise.all([
        callGroqForDiscoverContent(),
        fetchPerigonTrendingNews().catch((err) => {
          return null;
        }),
      ]);

      // Convert Perigon articles to NewsItems
      const newsPromise: Promise<PerigonNewsItem[]> = perigonData
        ? Promise.resolve(perigonData.articles.map(convertPerigonToNewsItem))
        : Promise.resolve([]);

      const topicsPromise: Promise<GroqTopicItem[]> = Promise.all(
        (groqData.topics || []).map(async (item, index) => {
          const keyword = item.imageKeyword || item.title;
          const image = (await fetchPixabayImage(keyword)) || item.image;
          return {
            ...item,
            id: item.id || `topic-${index + 1}`,
            description: item.description,
            category: item.category || 'General',
            image,
          };
        })
      );

      // Fetch weather using WeatherAPI with automatic location detection
      const [newsItems, topicsWithImages, liveWeather] = await Promise.all([
        newsPromise,
        topicsPromise,
        fetchWeatherByCity(), // No city parameter - will auto-detect location
      ]);

      // Use WeatherAPI data directly with all extended fields
      const normalizedWeather: WeatherData | null = liveWeather || null;

      const normalized: DiscoverContentPayload = {
        weather: normalizedWeather,
        dailyDigest: groqData.dailyDigest
          ? {
            ...groqData.dailyDigest,
            date:
              groqData.dailyDigest.date || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            items: groqData.dailyDigest.items || [],
          }
          : null,
        news: newsItems,
        topics: topicsWithImages,
        modeInsights: normalizeModeInsights(groqData.modeInsights),
        newsSummary: perigonData?.summary,
      };

      cachedDiscoverContent = normalized;
      saveToLocalStorage(normalized); // Persist to localStorage
      return normalized;
    } finally {
      pendingDiscoverRequest = null;
    }
  })();

  return pendingDiscoverRequest;
};

const normalizeDetailArray = <T>(value: unknown): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return [];
};

const normalizeDetailStats = (value: unknown): { label: string; value: string }[] => {
  return normalizeDetailArray<{ label: string; value: string }>(value).filter(
    (item) => typeof item?.label === 'string' && typeof item?.value === 'string'
  );
};

const normalizeDetailSections = (value: unknown): DiscoverDetailSection[] => {
  return normalizeDetailArray<DiscoverDetailSection>(value).filter(
    (item) => typeof item?.heading === 'string' && typeof item?.body === 'string'
  );
};

const normalizeDetailResources = (value: unknown): DiscoverDetailResource[] => {
  return normalizeDetailArray<DiscoverDetailResource>(value).filter(
    (item) => typeof item?.title === 'string' && typeof item?.description === 'string'
  );
};

const normalizeDetailActions = (value: unknown): DiscoverDetailAction[] => {
  return normalizeDetailArray<DiscoverDetailAction>(value).filter(
    (item) => typeof item?.label === 'string' && typeof item?.description === 'string'
  );
};

const VALID_DISCOVER_MODES: DiscoverMode[] = ['chat', 'image', 'web', 'deep-research'];

const normalizeModeInsights = (value: unknown): ModeInsight[] => {
  if (!Array.isArray(value)) return [];

  return (value as unknown[])
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;

      const rawMode = typeof raw.mode === 'string' ? (raw.mode as string) : VALID_DISCOVER_MODES[0];
      const mode = VALID_DISCOVER_MODES.includes(rawMode as DiscoverMode)
        ? (rawMode as DiscoverMode)
        : VALID_DISCOVER_MODES[0];

      const title = typeof raw.title === 'string' && raw.title.trim().length > 0 ? raw.title : 'Discover more';
      const summary = typeof raw.summary === 'string' ? raw.summary : '';
      const tips = Array.isArray(raw.tips)
        ? (raw.tips as unknown[]).filter((tip): tip is string => typeof tip === 'string' && tip.trim().length > 0)
        : [];
      const promptSuggestions = Array.isArray(raw.promptSuggestions)
        ? (raw.promptSuggestions as unknown[]).filter(
          (prompt): prompt is string => typeof prompt === 'string' && prompt.trim().length > 0
        )
        : [];
      const callToAction =
        typeof raw.callToAction === 'string' && raw.callToAction.trim().length > 0 ? raw.callToAction : undefined;

      return {
        mode,
        title,
        summary,
        tips,
        promptSuggestions,
        callToAction,
      } satisfies ModeInsight;
    })
    .filter(Boolean) as ModeInsight[];
};

export const fetchDiscoverDetailContent = async (
  type: DiscoverDetailType,
  baseData: Record<string, unknown>,
  forceRefresh = false
): Promise<DiscoverDetailContent> => {
  const identifier = baseData?.id || baseData?.title || baseData?.category || 'detail';
  const cacheKey = `${type}:${identifier}`;

  if (!forceRefresh) {
    const cached = detailCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = pendingDetailRequests.get(cacheKey);
    if (pending) {
      return pending;
    }
  }

  const serializedBase = JSON.stringify({ type, baseData });

  const request = (async () => {
    try {
      const response = await fetch(GROQ_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${GROQ_API_KEY}`,
        },
        body: JSON.stringify({
          model: 'openai/gpt-oss-120b',
          temperature: 0.7,
          max_completion_tokens: 3072,
          top_p: 1,
          stream: false,
          reasoning_effort: 'medium',
          messages: [
            {
              role: 'system',
              content:
                'You enrich discover content for a detail page. Respond with strict JSON and never include commentary or markdown fences.',
            },
            {
              role: 'user',
              content: `Create detailed content for a discover page detail view.
Type: ${type}
BaseData: ${serializedBase}

Return JSON with this shape:
{
  "summary": string,
  "keyTakeaways": string[],
  "sections": [
    { "heading": string, "body": string }
  ],
  "stats": [
    { "label": string, "value": string }
  ],
  "quote": { "text": string, "author": string }
  
}

Guidelines:
- Use engaging yet concise language tailored to ${type}.
- Provide 3-5 key takeaways and 2-4 sections.
- Stats should highlight relevant metrics (impact, forecast values, etc.).
- Include at least 2 resources when possible with credible URLs but not for weather info.
- Keep URLs only HTTPS when provided; use empty string if unavailable.
- Omit any fields you cannot populate by returning an empty array or null-equivalent in JSON.
- Ensure the JSON is valid and matches the schema exactly.`,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.text();
        throw new Error(`detail request failed: ${response.status} ${errorPayload}`);
      }

      const groqData = await response.json();
      const content: string | undefined = groqData?.choices?.[0]?.message?.content;

      if (!content) {
        throw new Error('detail response did not include content');
      }

      const cleaned = cleanJSONContent(content);
      const parsed = JSON.parse(cleaned);

      const detail: DiscoverDetailContent = {
        summary: typeof parsed.summary === 'string' ? parsed.summary : '',
        keyTakeaways: normalizeDetailArray<string>(parsed.keyTakeaways).filter((item) => typeof item === 'string'),
        sections: normalizeDetailSections(parsed.sections),
        stats: normalizeDetailStats(parsed.stats),
        resources: normalizeDetailResources(parsed.resources),
        actions: normalizeDetailActions(parsed.actions),
        quote:
          parsed.quote && typeof parsed.quote === 'object' && typeof parsed.quote.text === 'string'
            ? { text: parsed.quote.text, author: parsed.quote.author }
            : undefined,
      };

      detailCache.set(cacheKey, detail);
      return detail;
    } finally {
      pendingDetailRequests.delete(cacheKey);
    }
  })();

  pendingDetailRequests.set(cacheKey, request);
  return request;
};

export const fetchWeatherData = async (): Promise<WeatherData | null> => {
  const data = await fetchDiscoverContent();
  return data.weather;
};

export const fetchTrendingNews = async (): Promise<NewsItem[]> => {
  const data = await fetchDiscoverContent();
  return data.news.map(({ content, ...rest }) => rest);
};

export const fetchPersonalizedTopics = async (): Promise<TopicCard[]> => {
  const data = await fetchDiscoverContent();
  return data.topics.map(({ imageKeyword, ...rest }) => rest);
};

export const fetchDailyDigest = async (): Promise<DailyDigest | null> => {
  const data = await fetchDiscoverContent();
  return data.dailyDigest;
};

export const clearDiscoverCache = () => {
  cachedDiscoverContent = null;
  pendingDiscoverRequest = null;
};

export const clearDiscoverDetailCache = () => {
  detailCache.clear();
  pendingDetailRequests.clear();
};
